import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { TokenStorageService } from '@/core/services/internal/token-storage.service';
import { HttpClientService } from '@/core/services/http/http-methods/http-client.service';
import { GeneralUrlsService } from '@/core/services/http/api-url/general-urls.service';

import { User } from '@/core/models/user.model';
import { ProfileAdapter } from '@/core/adapters/profileAdapter';
import {AnalysisStoreService} from "@/core/services/internal/analysis-store.service";

@Injectable({
  providedIn: 'root'
})
export class SsoService {
  private user: User | null = null;

  /**
   * The RxJS BehaviorSubject is a special type of Subject that keeps hold
   * of the current value and emits it to any new subscribers as soon as
   * they subscribe, while regular Subjects don't store the current value
   * and only emit values that are published after a subscription is created.
   * @private
   */
  private currentUserSubject: BehaviorSubject<any>;
  /**
   * Angular components can subscribe() to the public currentUser: Observable
   * property to be notified of changes, and notifications are sent when the
   * this.currentUserSubject.next() method is called in the login() and logout()
   * methods, passing the argument to each subscriber
   * @public
   */
  public currentUser: Observable<any>;

  constructor(
    private httpClientService: HttpClientService,
    private endpointUrlsService: GeneralUrlsService,
    private profileAdapter: ProfileAdapter,
    private tokenStorageService: TokenStorageService,
  ) {
    this.currentUserSubject = new BehaviorSubject<User | null>(this.tokenStorageService.getUser());
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public sign_up(profile: object): Observable<User> {
    const url = this.endpointUrlsService.getPublicEndpoint('signUp');

    return this.httpClientService.post(url, profile)
        .pipe(
            map((data: any) => {
              return this.profileAdapter.adapt(data.User);
            })
        );
  }

  public login(profile: object): Observable<User> {
    const url = this.endpointUrlsService.getPublicEndpoint('login');
    return this.httpClientService.post(url, profile)
      .pipe(
        map((data: any) => {
          return  this.profileAdapter.adapt(data.User);
        })
      );
  }

  public logout(): Observable<any> {
    const url = this.endpointUrlsService.getPrivateEndpoint('logout');

    return this.httpClientService.post(url, {}).pipe(
        map((response: any) => {
          this.tokenStorageService.signOut();
          this.currentUserSubject.next(null);
          return response;
        })
    );
  }

  public validateSetupToken(payload: { token: string }): Observable<any> {
    const url = this.endpointUrlsService.getPublicEndpoint('validateSetupToken');

    return this.httpClientService.post(url, payload).pipe(
        map((response: any) => response)
    );
  }

  public setupPassword(payload: {
    token: string;
    password: string;
  }): Observable<any> {
    const url = this.endpointUrlsService.getPublicEndpoint('setupPassword');

    return this.httpClientService.post(url, payload).pipe(
        map((response: any) => {
          return response;
        })
    );
  }

  public getPendingAccounts(): Observable<any[]> {

    const url = this.endpointUrlsService.getPrivateEndpoint(
        'pendingAccounts'
    );

    return this.httpClientService.get(url).pipe(
        map((response: any) => {
          return response.accounts;
        })
    );
  }

  public validateAccount(userId: string): Observable<any> {

    const url = this.endpointUrlsService.getPrivateEndpoint(
        'validateAccount'
    );

    return this.httpClientService.post(
        url,
        {
          user_id: userId,
        }
    ).pipe(
        map((response: any) => {
          return response;
        })
    );
  }

  /**
   * Getter allows other components an easy way to get the value
   * of the currently logged-in user without having to subscribe
   * to the currentUser Observable.
   */
  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  /**
   * Update user profile
   */
  public updateUserValue(newProfile: User): void {
    this.currentUserSubject.next({...this.currentUserValue, ...newProfile});
  }
}
